/**
 * @fileoverview Data Layer (DL) for the "Cronómetro" game dynamic (Tile ID 50) of Club Atlético de San Luis (ADSL).
 * Provides standardized native HTTP fetch requests to communicate with the Cronometer API controller endpoints.
 * All functions return normalized payloads { success, data, message } and handle errors gracefully.
 * 
 * @module utilities/DL/Cronometer
 */

/**
 * Normalizes and returns the base API URL ensuring correct slash handling.
 * Works seamlessly whether REACT_APP_API_URL includes or omits trailing slashes.
 * 
 * @function getApiUrl
 * @param {string} endpoint - The relative API endpoint path.
 * @returns {string} Fully qualified URL to the requested endpoint.
 */
const getApiUrl = (endpoint) => {
    let baseUrl = process.env.REACT_APP_API_URL || '';
    if (!baseUrl) {
        baseUrl = '/api/';
    }
    if (!baseUrl.endsWith('/')) {
        baseUrl += '/';
    }

    // Clean leading slash from endpoint if present
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;

    // Prevent repeated '/api/' if baseUrl already ends with '/api/' and endpoint starts with 'api/'
    if (cleanEndpoint.startsWith('api/') && baseUrl.endsWith('/api/')) {
        return `${baseUrl}${cleanEndpoint.slice(4)}`;
    }

    return `${baseUrl}${cleanEndpoint}`;
};

// =============================================================================
// PUBLIC GAME & PARTICIPANT ENDPOINTS
// =============================================================================

/**
 * Retrieves the active operational configuration and theme for the Cronometer dynamic.
 * Used by the mobile view, stadium screen, and initial admin load.
 * GET /api/Cronometer/config
 * 
 * @async
 * @function GetPublicConfig
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>} API response payload.
 */
export const GetPublicConfig = async () => {
    try {
        const url = getApiUrl('Cronometer/config');
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al obtener la configuración de Cronómetro.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener la configuración.'
        };
    }
};

/**
 * Alias for GetPublicConfig.
 */
export const GetConfig = GetPublicConfig;

/**
 * Registers or idempotently retrieves a participant for the Cronometer dynamic.
 * POST /api/Cronometer/register
 * 
 * @async
 * @function RegisterParticipant
 * @param {Object} payload - Participant registration data.
 * @param {string} payload.name - Full name of the fan.
 * @param {string} payload.email - Normalized lowercase email address.
 * @param {string} payload.phone - 10-digit normalized phone number.
 * @param {string} [payload.abonado] - 'Sí' or 'No'.
 * @param {boolean} payload.acceptedTerms - Must be true.
 * @param {boolean} [payload.acceptedMarketing] - Marketing consent.
 * @param {string} [payload.trafficSource] - Origin source (e.g. 'qr_estadio').
 * @returns {Promise<{success: boolean, participantId?: number, phaseReached?: number, isWinner?: boolean, message?: string}>}
 */
export const RegisterParticipant = async (payload) => {
    try {
        const url = getApiUrl('Cronometer/register');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(payload)
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al procesar el registro del participante.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al registrar al participante.'
        };
    }
};

/**
 * Asynchronously logs a dual telemetry metric in the backend database.
 * POST /api/Cronometer/track-metric
 * 
 * @async
 * @function TrackMetric
 * @param {Object} metricPayload - Telemetry data object.
 * @param {string} metricPayload.eventName - Event identifier (e.g. 'cronometer_qr_scan').
 * @param {string} [metricPayload.sessionId] - Client session or socket identifier.
 * @param {string} [metricPayload.source] - Origin channel ('adsl-website-fan', 'screen', etc.).
 * @param {string} [metricPayload.additionalDataJson] - Serialized JSON context.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const TrackMetric = async (metricPayload) => {
    try {
        const url = getApiUrl('Cronometer/track-metric');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(metricPayload)
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al registrar métrica de telemetría.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al enviar telemetría.'
        };
    }
};

// =============================================================================
// THEMES CRUD & ASSET UPLOAD ENDPOINTS
// =============================================================================

/**
 * Retrieves the full catalogue of visual themes for Cronometer.
 * GET /api/Cronometer/themes
 * 
 * @async
 * @function GetThemes
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetThemes = async () => {
    try {
        const url = getApiUrl('Cronometer/themes');
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al obtener la lista de temas.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener temas.'
        };
    }
};

/**
 * Retrieves a single theme by its unique numeric identifier.
 * GET /api/Cronometer/themes/{id}
 * 
 * @async
 * @function GetThemeById
 * @param {number|string} id - Theme ID.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetThemeById = async (id) => {
    try {
        const cleanId = encodeURIComponent(String(id).trim());
        const url = getApiUrl(`Cronometer/themes/${cleanId}`);
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al obtener el tema seleccionado.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener el tema.'
        };
    }
};

/**
 * Creates a new visual theme for Cronometer.
 * POST /api/Cronometer/themes
 * 
 * @async
 * @function CreateTheme
 * @param {Object} themeData - Theme attributes.
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const CreateTheme = async (themeData) => {
    try {
        const url = getApiUrl('Cronometer/themes');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(themeData)
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al crear el tema visual.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al crear el tema.'
        };
    }
};

/**
 * Updates an existing visual theme.
 * PUT /api/Cronometer/themes/{id}
 * 
 * @async
 * @function UpdateTheme
 * @param {number|string} id - Theme ID.
 * @param {Object} themeData - Updated theme attributes.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const UpdateTheme = async (id, themeData) => {
    try {
        const cleanId = encodeURIComponent(String(id).trim());
        const url = getApiUrl(`Cronometer/themes/${cleanId}`);
        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(themeData)
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al actualizar el tema.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al actualizar el tema.'
        };
    }
};

/**
 * Deletes a non-active visual theme by ID.
 * DELETE /api/Cronometer/themes/{id}
 * 
 * @async
 * @function DeleteTheme
 * @param {number|string} id - Theme ID.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const DeleteTheme = async (id) => {
    try {
        const cleanId = encodeURIComponent(String(id).trim());
        const url = getApiUrl(`Cronometer/themes/${cleanId}`);
        const response = await fetch(url, {
            method: 'DELETE',
            headers: {
                'Accept': 'application/json'
            }
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al eliminar el tema.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al eliminar el tema.'
        };
    }
};

/**
 * Uploads an image file to be optimized via SkiaSharp on the backend (WebP, max 2560px, Magic Bytes validation).
 * POST /api/Cronometer/themes/upload-asset
 * 
 * @async
 * @function UploadThemeAsset
 * @param {File|Blob} file - Binary image file.
 * @returns {Promise<{success: boolean, url?: string, width?: number, height?: number, format?: string, sizeBytes?: number, message?: string}>}
 */
export const UploadThemeAsset = async (file) => {
    try {
        if (!file) {
            return { success: false, message: 'Debe seleccionar un archivo válido.' };
        }

        const formData = new FormData();
        formData.append('file', file);

        const url = getApiUrl('Cronometer/themes/upload-asset');
        const response = await fetch(url, {
            method: 'POST',
            // Note: Do NOT set Content-Type header so the browser sets multipart/form-data boundary automatically
            body: formData
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al subir y procesar la imagen.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al subir la imagen.'
        };
    }
};

/**
 * Sets the active visual theme for the current matchday dynamic.
 * POST /api/Cronometer/admin/set-active-theme
 * 
 * @async
 * @function SetActiveTheme
 * @param {number|string} themeId - ID of the theme to activate.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const SetActiveTheme = async (themeId) => {
    try {
        const parsedId = Number(themeId);
        if (isNaN(parsedId) || parsedId <= 0) {
            return { success: false, message: 'Identificador de tema inválido.' };
        }

        const url = getApiUrl('Cronometer/admin/set-active-theme');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({ themeId: parsedId })
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al activar el tema.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al activar el tema.'
        };
    }
};

// =============================================================================
// ADMIN CONFIGURATION, MONITORING & REPORTING ENDPOINTS
// =============================================================================

/**
 * Updates the game configuration from the admin operator console.
 * PUT /api/Cronometer/admin/config
 * 
 * @async
 * @function UpdateAdminConfig
 * @param {Object} configData - Updated game configuration parameters.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const UpdateAdminConfig = async (configData) => {
    try {
        const url = getApiUrl('Cronometer/admin/config');
        const response = await fetch(url, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify(configData)
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al actualizar la configuración operativa.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al actualizar la configuración.'
        };
    }
};

/**
 * Retrieves the full participant registry with Mexico CST formatted timestamps and phase times.
 * GET /api/Cronometer/admin/records?search={search}&filter={filter}
 * 
 * @async
 * @function GetAdminRecords
 * @param {string} [search=''] - Search term for name, email or phone.
 * @param {string} [filter=''] - Filter category ('all', 'winners', 'online', 'abonados').
 * @returns {Promise<{success: boolean, data?: Array<Object>, message?: string}>}
 */
export const GetAdminRecords = async (search = '', filter = '') => {
    try {
        const queryParams = new URLSearchParams();
        if (search && String(search).trim() !== '') {
            queryParams.append('search', String(search).trim());
        }
        if (filter && String(filter).trim() !== '') {
            queryParams.append('filter', String(filter).trim());
        }

        const queryString = queryParams.toString();
        const url = getApiUrl(`Cronometer/admin/records${queryString ? `?${queryString}` : ''}`);

        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al consultar el padrón de participantes.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al consultar participantes.'
        };
    }
};

/**
 * Retrieves live connection metrics (CurrentlyActive, TotalEverConnected, TotalLeft, TotalRegistered, RetentionRate).
 * GET /api/Cronometer/admin/connection-stats
 * 
 * @async
 * @function GetConnectionStats
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetConnectionStats = async () => {
    try {
        const url = getApiUrl('Cronometer/admin/connection-stats');
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al obtener estadísticas de conexión.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener estadísticas.'
        };
    }
};

/**
 * Retrieves the 9 conversion funnel KPIs and aggregate totals for the admin dashboard.
 * GET /api/Cronometer/admin/stats
 * 
 * @async
 * @function GetAdminStats
 * @returns {Promise<{success: boolean, data?: Object, message?: string}>}
 */
export const GetAdminStats = async () => {
    try {
        const url = getApiUrl('Cronometer/admin/stats');
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Accept': 'application/json'
            }
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al obtener estadísticas del embudo.'
            };
        }

        return await response.json();
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al obtener estadísticas del embudo.'
        };
    }
};

/**
 * Resets the active Cronometer dynamic, clearing live RAM counters and game states back to Phase 1.
 * POST /api/Cronometer/admin/reset
 * 
 * @async
 * @function ResetGame
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const ResetGame = async () => {
    try {
        const url = getApiUrl('Cronometer/admin/reset');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            }
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al reiniciar la dinámica.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al reiniciar el juego.'
        };
    }
};

/**
 * Purges all participants, phase records, and metrics from the database and resets counters.
 * Requires master password authorization ('').
 * POST /api/Cronometer/admin/purge-all
 * 
 * @async
 * @function PurgeAllDynamicData
 * @param {string} masterPassword - Password for administrative confirmation.
 * @param {string} [operatorName='Admin'] - Operator responsible for the action.
 * @returns {Promise<{success: boolean, message?: string}>}
 */
export const PurgeAllDynamicData = async (masterPassword, operatorName = 'Admin') => {
    try {
        const url = getApiUrl('Cronometer/admin/purge-all');
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json'
            },
            body: JSON.stringify({
                masterPassword: masterPassword ? masterPassword.trim() : '',
                operatorName: operatorName ? operatorName.trim() : 'Admin'
            })
        });

        const result = await response.json().catch(() => ({}));

        if (!response.ok) {
            return {
                success: false,
                message: result.message || 'Error al purgar los datos de la dinámica.'
            };
        }

        return result;
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al purgar la dinámica.'
        };
    }
};

/**
 * Downloads the official executive Excel report (.xlsx) with closedXML formatting and Mexico CST dates.
 * Dispatches automatic browser download via Blob and returns { success, data: blob, fileName }.
 * GET /api/Cronometer/admin/export
 * 
 * @async
 * @function ExportExcelReport
 * @returns {Promise<{success: boolean, data?: Blob, fileName?: string, message?: string}>}
 */
export const ExportExcelReport = async () => {
    try {
        const url = getApiUrl('Cronometer/admin/export');
        const response = await fetch(url, {
            method: 'GET'
        });

        if (!response.ok) {
            const errorPayload = await response.json().catch(() => ({}));
            return {
                success: false,
                message: errorPayload.message || 'Error al exportar el reporte en Excel.'
            };
        }

        const blob = await response.blob();

        // Extract filename from Content-Disposition header if available
        let fileName = `Reporte_Cronometro_${new Date().toISOString().slice(0, 10)}.xlsx`;
        const contentDisposition = response.headers.get('content-disposition');
        if (contentDisposition && contentDisposition.includes('filename=')) {
            const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
            if (matches && matches[1]) {
                fileName = matches[1].replace(/['"]/g, '').trim();
            }
        }

        // Trigger browser file download if DOM is accessible
        if (typeof window !== 'undefined' && window.document) {
            const downloadUrl = window.URL.createObjectURL(blob);
            const anchorElement = document.createElement('a');
            anchorElement.style.display = 'none';
            anchorElement.href = downloadUrl;
            anchorElement.download = fileName;
            document.body.appendChild(anchorElement);
            anchorElement.click();
            window.URL.revokeObjectURL(downloadUrl);
            document.body.removeChild(anchorElement);
        }

        return {
            success: true,
            data: blob,
            fileName: fileName,
            message: 'Reporte exportado exitosamente.'
        };
    } catch (networkError) {
        return {
            success: false,
            message: networkError.message || 'Error de conexión al exportar el reporte.'
        };
    }
};

/**
 * Alias for ExportExcelReport.
 */
export const ExportExcel = ExportExcelReport;

export default {
    GetPublicConfig,
    GetConfig,
    RegisterParticipant,
    TrackMetric,
    GetThemes,
    GetThemeById,
    CreateTheme,
    UpdateTheme,
    DeleteTheme,
    UploadThemeAsset,
    SetActiveTheme,
    UpdateAdminConfig,
    GetAdminRecords,
    GetConnectionStats,
    GetAdminStats,
    ResetGame,
    PurgeAllDynamicData,
    ExportExcelReport,
    ExportExcel
};
